import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  Container,
  CardContent
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  LocalHospital as DiagnosisIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import { debounce } from 'lodash';
import { useUserData } from '../../hooks/useUserData';
import { createCancerDiagnosis, updateCancerDiagnosis, getCancerDiagnosis } from '../../services/oncologyService';
import { createEpisodeFromDiagnosis } from '../../services/episodeOfCareService';
import { searchProcedureCodesByType } from '../../services/procedureCodesService';
import { PageHeader, PageContainer, ContentCard } from './oncologyStyles';
import {
  CANCER_TYPES,
  T_STAGES,
  N_STAGES,
  M_STAGES,
  AJCC_STAGES,
  BIOMARKERS,
  BIOMARKER_RESULTS,
  T_STAGE_DESCRIPTIONS,
  N_STAGE_DESCRIPTIONS,
  M_STAGE_DESCRIPTIONS,
  AJCC_STAGE_DESCRIPTIONS
} from '../../types/enums';

const CancerDiagnosisForm = ({ patientId: propPatientId, existingDiagnosis: propExistingDiagnosis, onSave, onCancel }) => {
  const { patientId: urlPatientId, diagnosisId } = useParams();
  const navigate = useNavigate();
  const patientId = propPatientId || urlPatientId;
  const { defaultFacility, currentUser } = useUserData();
  const [loading, setLoading] = useState(false);
  const [loadingDiagnosis, setLoadingDiagnosis] = useState(!!diagnosisId);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [existingDiagnosis, setExistingDiagnosis] = useState(propExistingDiagnosis || null);
  
  // ICD-10 search state
  const [icd10Options, setIcd10Options] = useState([]);
  const [icd10Loading, setIcd10Loading] = useState(false);
  const [icd10InputValue, setIcd10InputValue] = useState('');

  const [formData, setFormData] = useState({
    cancerType: '',
    primarySite: '',
    histology: '',
    histologyCode: '',
    grade: '',
    laterality: '',
    diagnosisDate: new Date().toISOString().split('T')[0],
    staging: {
      tStage: '',
      nStage: '',
      mStage: '',
      clinicalStage: '',
      pathologicalStage: '',
      stagingSystem: 'AJCC 8th Edition',
      stagingDate: new Date().toISOString().split('T')[0]
    },
    biomarkers: [],
    metastaticSites: [],
    icd10Codes: [], // Array of { code, description }
    notes: ''
  });

  // Load existing diagnosis if editing
  useEffect(() => {
    const loadDiagnosis = async () => {
      if (!diagnosisId || propExistingDiagnosis) return;
      
      try {
        setLoadingDiagnosis(true);
        const result = await getCancerDiagnosis(diagnosisId);
        setExistingDiagnosis(result);
      } catch (err) {
        console.error('Error loading diagnosis:', err);
        setError('Failed to load diagnosis');
      } finally {
        setLoadingDiagnosis(false);
      }
    };
    
    loadDiagnosis();
  }, [diagnosisId, propExistingDiagnosis]);

  // Populate form when existingDiagnosis is available
  useEffect(() => {
    if (existingDiagnosis) {
      // Handle backwards compatibility: convert old single icd10Code to array format
      let icd10Codes = existingDiagnosis.icd10Codes || [];
      if (!icd10Codes.length && existingDiagnosis.icd10Code) {
        icd10Codes = [{
          code: existingDiagnosis.icd10Code,
          description: existingDiagnosis.icd10Description || existingDiagnosis.icd10Code
        }];
      }
      
      setFormData({
        ...existingDiagnosis,
        diagnosisDate: existingDiagnosis.diagnosisDate?.split('T')[0] || '',
        staging: {
          ...existingDiagnosis.staging,
          stagingDate: existingDiagnosis.staging?.stagingDate?.split('T')[0] || ''
        },
        icd10Codes
      });
    }
  }, [existingDiagnosis]);

  // Debounced ICD-10 search
  const debouncedIcd10Search = useMemo(
    () => debounce(async (searchTerm) => {
      if (!searchTerm || searchTerm.length < 2) {
        setIcd10Options([]);
        setIcd10Loading(false);
        return;
      }

      setIcd10Loading(true);
      try {
        const results = await searchProcedureCodesByType('ICD-10', searchTerm, true);
        setIcd10Options(results.slice(0, 20));
      } catch (err) {
        console.error('Error searching ICD-10 codes:', err);
        setIcd10Options([]);
      } finally {
        setIcd10Loading(false);
      }
    }, 300),
    []
  );

  useEffect(() => {
    debouncedIcd10Search(icd10InputValue);
  }, [icd10InputValue, debouncedIcd10Search]);

  useEffect(() => {
    return () => {
      debouncedIcd10Search.cancel();
    };
  }, [debouncedIcd10Search]);

  // Handle ICD-10 selection - add to array
  const handleIcd10Select = (event, newValue) => {
    if (newValue) {
      // Check if already added
      const alreadyAdded = formData.icd10Codes.some(c => c.code === newValue.procedure_code);
      if (!alreadyAdded) {
        setFormData(prev => ({
          ...prev,
          icd10Codes: [...prev.icd10Codes, {
            code: newValue.procedure_code,
            description: newValue.name
          }]
        }));
      }
      setIcd10InputValue('');
    }
  };

  // Remove ICD-10 code
  const handleRemoveIcd10 = (codeToRemove) => {
    setFormData(prev => ({
      ...prev,
      icd10Codes: prev.icd10Codes.filter(c => c.code !== codeToRemove)
    }));
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleStagingChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      staging: {
        ...prev.staging,
        [field]: value
      }
    }));
  };

  const handleAddBiomarker = () => {
    setFormData(prev => ({
      ...prev,
      biomarkers: [
        ...prev.biomarkers,
        {
          biomarker: '',
          result: '',
          value: '',
          testDate: new Date().toISOString().split('T')[0],
          notes: ''
        }
      ]
    }));
  };

  const handleBiomarkerChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      biomarkers: prev.biomarkers.map((bm, i) => 
        i === index ? { ...bm, [field]: value } : bm
      )
    }));
  };

  const handleRemoveBiomarker = (index) => {
    setFormData(prev => ({
      ...prev,
      biomarkers: prev.biomarkers.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.cancerType || !formData.primarySite || !formData.diagnosisDate) {
      setError('Please fill in all required fields');
      return;
    }

    if (!formData.staging.tStage || !formData.staging.nStage || !formData.staging.mStage) {
      setError('Please complete TNM staging');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const diagnosisData = {
        ...formData,
        patientId: existingDiagnosis?.patientId || patientId,
        facilityId: defaultFacility.id
      };

      let result;
      if (existingDiagnosis?.id) {
        result = await updateCancerDiagnosis(existingDiagnosis.id, diagnosisData);
      } else {
        result = await createCancerDiagnosis(diagnosisData);
        
        // Auto-create Episode of Care for new diagnoses
        if (result?.id) {
          try {
            await createEpisodeFromDiagnosis(
              diagnosisData.patientId,
              result.id,
              defaultFacility.id,
              {
                episodeName: `${formData.cancerType} Treatment ${new Date().getFullYear()}`,
                currentPhase: 'Diagnosis'
              }
            );
            console.log('Episode of Care auto-created for diagnosis:', result.id);
          } catch (episodeError) {
            // Log but don't fail the diagnosis creation
            console.error('Failed to auto-create Episode of Care:', episodeError);
          }
        }
      }

      setSuccess(true);
      if (onSave) {
        onSave(result);
      } else {
        // Navigate back after a brief delay to show success message
        setTimeout(() => {
          // Go to the facility-wide diagnoses list
          navigate('/oncology/diagnoses');
        }, 1500);
      }
    } catch (err) {
      console.error('Error saving cancer diagnosis:', err);
      setError(err.message || 'Failed to save cancer diagnosis');
    } finally {
      setLoading(false);
    }
  };

  const grades = ['Well Differentiated (G1)', 'Moderately Differentiated (G2)', 'Poorly Differentiated (G3)', 'Undifferentiated (G4)', 'Not Assessed'];
  const lateralities = ['Left', 'Right', 'Bilateral', 'Not Applicable'];
  const metastaticSiteOptions = ['Bone', 'Liver', 'Lung', 'Brain', 'Lymph Nodes', 'Peritoneum', 'Adrenal', 'Skin', 'Other'];

  // Show loading while fetching existing diagnosis
  if (loadingDiagnosis) {
    return (
      <PageContainer>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
            <CircularProgress />
          </Box>
        </Container>
      </PageContainer>
    );
  }

  // Show message if no patient selected (and not editing existing)
  if (!patientId && !diagnosisId) {
    return (
      <PageContainer>
        <Container maxWidth="lg">
          <PageHeader
            title="New Cancer Diagnosis"
            subtitle="Create a new cancer diagnosis record"
            icon={<DiagnosisIcon />}
            backTo="/oncology/diagnoses"
          />
          <ContentCard>
            <CardContent>
              <Alert severity="info" sx={{ mb: 2 }}>
                Please select a patient to create a new cancer diagnosis.
              </Alert>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                To create a new diagnosis, navigate to a patient's profile and use the Oncology tab to add a diagnosis.
              </Typography>
              <Button variant="outlined" onClick={() => navigate('/patients')}>
                Go to Patients
              </Button>
            </CardContent>
          </ContentCard>
        </Container>
      </PageContainer>
    );
  }

  // Main form - with patient selected
  return (
    <PageContainer>
      <Container maxWidth="lg">
        <PageHeader
          title={diagnosisId ? 'Edit Cancer Diagnosis' : 'New Cancer Diagnosis'}
          subtitle="Cancer diagnosis documentation and staging"
          icon={<DiagnosisIcon />}
          backTo={patientId ? `/patients/${patientId}` : '/oncology/diagnoses'}
          onBack={onCancel}
        />
        <ContentCard>
          <CardContent>
            {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Cancer diagnosis saved successfully!
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        {/* Primary Diagnosis */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5, color: 'primary.main' }}>
          Primary Diagnosis
        </Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth required size="small">
              <InputLabel>Cancer Type</InputLabel>
              <Select
                value={formData.cancerType}
                label="Cancer Type"
                onChange={(e) => handleChange('cancerType', e.target.value)}
              >
                {CANCER_TYPES.map(type => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              required
              size="small"
              label="Primary Site"
              value={formData.primarySite}
              onChange={(e) => handleChange('primarySite', e.target.value)}
              placeholder="e.g., Right breast upper outer quadrant"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <DatePicker
              label="Diagnosis Date"
              value={formData.diagnosisDate ? new Date(formData.diagnosisDate) : null}
              onChange={(date) => handleChange('diagnosisDate', date ? date.toISOString().split('T')[0] : '')}
              slotProps={{
                textField: { fullWidth: true, size: 'small', required: true }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              required
              size="small"
              label="Histology"
              value={formData.histology}
              onChange={(e) => handleChange('histology', e.target.value)}
              placeholder="e.g., Invasive ductal carcinoma"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Histology Code (ICD-O-3)"
              value={formData.histologyCode}
              onChange={(e) => handleChange('histologyCode', e.target.value)}
              placeholder="e.g., 8500/3"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Grade</InputLabel>
              <Select
                value={formData.grade}
                label="Grade"
                onChange={(e) => handleChange('grade', e.target.value)}
              >
                {grades.map(grade => (
                  <MenuItem key={grade} value={grade}>{grade}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Laterality</InputLabel>
              <Select
                value={formData.laterality}
                label="Laterality"
                onChange={(e) => handleChange('laterality', e.target.value)}
              >
                {lateralities.map(lat => (
                  <MenuItem key={lat} value={lat}>{lat}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* TNM Staging */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5, color: 'secondary.main' }}>
          TNM Staging (AJCC 8th Edition)
        </Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={4} sm={4} md={2}>
            <FormControl fullWidth required size="small">
              <InputLabel>T Stage</InputLabel>
              <Select
                value={formData.staging.tStage}
                label="T Stage"
                onChange={(e) => handleStagingChange('tStage', e.target.value)}
              >
                {T_STAGES.map(stage => (
                  <MenuItem key={stage} value={stage}>
                    <Tooltip title={T_STAGE_DESCRIPTIONS[stage]} placement="right">
                      <span style={{ width: '100%' }}>{stage}</span>
                    </Tooltip>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={4} sm={4} md={2}>
            <FormControl fullWidth required size="small">
              <InputLabel>N Stage</InputLabel>
              <Select
                value={formData.staging.nStage}
                label="N Stage"
                onChange={(e) => handleStagingChange('nStage', e.target.value)}
              >
                {N_STAGES.map(stage => (
                  <MenuItem key={stage} value={stage}>
                    <Tooltip title={N_STAGE_DESCRIPTIONS[stage]} placement="right">
                      <span style={{ width: '100%' }}>{stage}</span>
                    </Tooltip>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={4} sm={4} md={2}>
            <FormControl fullWidth required size="small">
              <InputLabel>M Stage</InputLabel>
              <Select
                value={formData.staging.mStage}
                label="M Stage"
                onChange={(e) => handleStagingChange('mStage', e.target.value)}
              >
                {M_STAGES.map(stage => (
                  <MenuItem key={stage} value={stage}>
                    <Tooltip title={M_STAGE_DESCRIPTIONS[stage]} placement="right">
                      <span style={{ width: '100%' }}>{stage}</span>
                    </Tooltip>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Clinical Stage</InputLabel>
              <Select
                value={formData.staging.clinicalStage}
                label="Clinical Stage"
                onChange={(e) => handleStagingChange('clinicalStage', e.target.value)}
              >
                {AJCC_STAGES.map(stage => (
                  <MenuItem key={stage} value={stage}>{stage}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Path Stage</InputLabel>
              <Select
                value={formData.staging.pathologicalStage}
                label="Path Stage"
                onChange={(e) => handleStagingChange('pathologicalStage', e.target.value)}
              >
                {AJCC_STAGES.map(stage => (
                  <MenuItem key={stage} value={stage}>{stage}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <DatePicker
              label="Staging Date"
              value={formData.staging.stagingDate ? new Date(formData.staging.stagingDate) : null}
              onChange={(date) => handleStagingChange('stagingDate', date ? date.toISOString().split('T')[0] : '')}
              slotProps={{
                textField: { fullWidth: true, size: 'small' }
              }}
            />
          </Grid>
        </Grid>

        {/* Metastatic Sites */}
        {formData.staging.mStage && formData.staging.mStage !== 'M0' && formData.staging.mStage !== 'MX' && (
          <Box sx={{ mt: 1.5 }}>
            <Autocomplete
              multiple
              size="small"
              options={metastaticSiteOptions}
              value={formData.metastaticSites}
              onChange={(e, newValue) => handleChange('metastaticSites', newValue)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip variant="outlined" size="small" label={option} {...getTagProps({ index })} />
                ))
              }
              renderInput={(params) => (
                <TextField {...params} size="small" label="Metastatic Sites" placeholder="Select sites" />
              )}
            />
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Biomarkers */}
        <Accordion defaultExpanded={formData.biomarkers.length > 0} sx={{ boxShadow: 'none', border: '1px solid', borderColor: 'divider', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { my: 1 } }}>
            <Typography variant="subtitle1" fontWeight="bold" color="info.main">
              Biomarkers ({formData.biomarkers.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            {formData.biomarkers.map((biomarker, index) => (
              <Box key={index} sx={{ mb: 1, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={6} sm={4} md={2.5}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Biomarker</InputLabel>
                      <Select
                        value={biomarker.biomarker}
                        label="Biomarker"
                        onChange={(e) => handleBiomarkerChange(index, 'biomarker', e.target.value)}
                      >
                        {BIOMARKERS.map(bm => (
                          <MenuItem key={bm} value={bm}>{bm}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6} sm={4} md={1.5}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Result</InputLabel>
                      <Select
                        value={biomarker.result}
                        label="Result"
                        onChange={(e) => handleBiomarkerChange(index, 'result', e.target.value)}
                      >
                        {BIOMARKER_RESULTS.map(result => (
                          <MenuItem key={result} value={result}>{result}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={4} sm={3} md={1.5}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Value"
                      value={biomarker.value}
                      onChange={(e) => handleBiomarkerChange(index, 'value', e.target.value)}
                      placeholder="e.g., 90%"
                    />
                  </Grid>
                  <Grid item xs={4} sm={3} md={2}>
                    <DatePicker
                      label="Test Date"
                      value={biomarker.testDate ? new Date(biomarker.testDate) : null}
                      onChange={(date) => handleBiomarkerChange(index, 'testDate', date ? date.toISOString().split('T')[0] : '')}
                      slotProps={{
                        textField: { fullWidth: true, size: 'small' }
                      }}
                    />
                  </Grid>
                  <Grid item xs={3} sm={2} md={3.5}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Notes"
                      value={biomarker.notes}
                      onChange={(e) => handleBiomarkerChange(index, 'notes', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={1}>
                    <IconButton 
                      color="error" 
                      onClick={() => handleRemoveBiomarker(index)}
                      size="small"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Grid>
                </Grid>
              </Box>
            ))}
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddBiomarker}
              variant="outlined"
              size="small"
            >
              Add Biomarker
            </Button>
          </AccordionDetails>
        </Accordion>

        <Divider sx={{ my: 2 }} />

        {/* Additional Information */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5 }}>
          Additional Information
        </Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              value={null}
              onChange={handleIcd10Select}
              inputValue={icd10InputValue}
              onInputChange={(event, newValue) => setIcd10InputValue(newValue)}
              options={icd10Options.filter(opt => !formData.icd10Codes.some(c => c.code === opt.procedure_code))}
              loading={icd10Loading}
              size="small"
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                return `${option.procedure_code} - ${option.name}`;
              }}
              renderOption={(props, option) => {
                const { key, ...restProps } = props;
                return (
                  <Box component="li" key={key} {...restProps}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip 
                          label="ICD-10" 
                          size="small" 
                          variant="outlined"
                          color="info"
                          sx={{ fontSize: '0.7rem', height: 18 }}
                        />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {option.procedure_code}
                        </Typography>
                      </Box>
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ 
                          mt: 0.25,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {option.name}
                      </Typography>
                    </Box>
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Add ICD-10 Code"
                  placeholder="Search by code or description..."
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <SearchIcon sx={{ color: 'action.active', mr: 0.5, fontSize: 20 }} />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                    endAdornment: (
                      <>
                        {icd10Loading ? <CircularProgress color="inherit" size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              noOptionsText={
                icd10InputValue.length < 2 
                  ? "Type 2+ characters to search..." 
                  : "No matching ICD-10 codes found"
              }
              loadingText="Searching..."
              clearOnBlur
              blurOnSelect
            />
            {/* Selected ICD-10 Codes */}
            {formData.icd10Codes.length > 0 && (
              <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {formData.icd10Codes.map((icd) => (
                  <Tooltip key={icd.code} title={icd.description} placement="top">
                    <Chip
                      label={icd.code}
                      size="small"
                      color="info"
                      variant="outlined"
                      onDelete={() => handleRemoveIcd10(icd.code)}
                      sx={{ 
                        '& .MuiChip-label': { fontWeight: 500 },
                        '& .MuiChip-deleteIcon': { fontSize: 16 }
                      }}
                    />
                  </Tooltip>
                ))}
              </Box>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              multiline
              rows={2}
              label="Clinical Notes"
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Additional clinical notes..."
            />
          </Grid>
        </Grid>

        {/* Actions */}
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
          {onCancel && (
            <Button variant="outlined" size="small" onClick={onCancel} disabled={loading}>
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            variant="contained"
            size="small"
            startIcon={loading ? <CircularProgress size={16} /> : <SaveIcon />}
            disabled={loading}
            sx={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4190 100%)',
              }
            }}
          >
            {loading ? 'Saving...' : 'Save Diagnosis'}
          </Button>
        </Box>
      </form>
          </CardContent>
        </ContentCard>
      </Container>
    </PageContainer>
  );
};

export default CancerDiagnosisForm;

